import addMonths from "date-fns/addMonths";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import endOfMonth from "date-fns/endOfMonth";
import endOfWeek from "date-fns/endOfWeek";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import isSameMonth from "date-fns/isSameMonth";
import isToday from "date-fns/isToday";
import startOfMonth from "date-fns/startOfMonth";
import startOfWeek from "date-fns/startOfWeek";
import subMonths from "date-fns/subMonths";
import React, { useEffect, useState, useCallback } from "react";
import {
  sendBookingConfirmationEmail,
  logBookingPayment,
} from "../../../components/api/api";
import "./BookingCalendar.scss";
import BookingModal from "./BookingModal";
import Button from "../../../components/Button/Button";

const API_URL = process.env.REACT_APP_BACKEND_URL || "/api";

export default function BookingCalendar({
  calendarId,
  propertyType,
  propertyId,
  propertyConfig,
  bookingBounds = { MAX_FUTURE_DAYS: 60, MAX_BOOKING_DURATION: 5 },
}) {
  const displayName = propertyConfig?.displayName || propertyType;
  const targetCalendarId = calendarId || process.env.REACT_APP_CALENDAR_ID;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState("Select your check-in date");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Two-phase booking state
  const [selectionPhase, setSelectionPhase] = useState("checkin"); // 'checkin' | 'checkout'
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [checkInAvailability, setCheckInAvailability] = useState([]);
  const [checkOutAvailability, setCheckOutAvailability] = useState([]);

  // Helper function to get all dates in a range
  const getDatesInRange = useCallback((start, end) => {
    const dates = [];
    let currentDate = new Date(start);

    while (currentDate < end) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }, []);

  // Load booked dates for the current month
  const loadBookedDates = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage("");

    try {
      const startOfCurrentMonth = startOfMonth(currentDate);
      const endOfCurrentMonth = endOfMonth(currentDate);
      const startDateStr = format(startOfCurrentMonth, "yyyy-MM-dd");
      const endDateStr = format(endOfCurrentMonth, "yyyy-MM-dd");

      // Fetch from both Google Calendar and iCal sources
      const promises = [];

      // Google Calendar - fetch booking periods
      const googleCalendarPromise = fetch(
        `${API_URL}/check-availability?startDate=${startDateStr}&endDate=${endDateStr}${targetCalendarId ? `&calendarId=${targetCalendarId}` : ""}`,
      )
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Google Calendar API error: ${res.status}`);
          }
          return res.json();
        })
        .catch((error) => {
          console.warn("Google Calendar fetch failed:", error.message);
          return { bookingPeriods: [] };
        });

      promises.push(googleCalendarPromise);

      // iCal sources - configure based on property type
      const icalUrls = [];

      if (propertyType === "Yurt") {
        icalUrls.push(
          "https://calendar.google.com/calendar/ical/e9m22pj2r04p6q067ptr7pbh1pomtrse%40import.calendar.google.com/public/basic.ics",
        );
      } else if (propertyType === "Dome") {
        icalUrls.push(
          "https://www.airbnb.ca/calendar/ical/1041725069586524761.ics?s=fe70e9636431233fba486b4fd80c04dd",
        );
      }

      // Add iCal requests for each URL
      icalUrls.forEach((icalUrl) => {
        const icalPromise = fetch(
          `${API_URL}/get-ical-dates?icalUrl=${encodeURIComponent(
            icalUrl,
          )}&startDate=${startDateStr}&endDate=${endDateStr}`,
        )
          .then((res) => {
            if (!res.ok) {
              throw new Error(`iCal API error: ${res.status}`);
            }
            return res.json();
          })
          .catch((error) => {
            console.warn("iCal fetch failed:", error.message);
            return { bookingPeriods: [] };
          });
        promises.push(icalPromise);
      });

      // Wait for all requests to complete
      const results = await Promise.all(promises);

      // Check if all requests failed
      const allFailed = results.every((result) => !result.bookingPeriods);
      if (allFailed && results.length > 0) {
        throw new Error("Unable to load availability data from any source");
      }

      // Combine all booked periods from all sources
      let allBookedPeriods = [];

      results.forEach((result) => {
        if (result.bookingPeriods) {
          allBookedPeriods.push(...result.bookingPeriods);
        }
      });

      // Build lists of blocked dates for check-in and check-out
      const blockedCheckInDays = [];
      const blockedCheckOutDays = [];

      allBookedPeriods.forEach((period) => {
        const start = new Date(period.startDate);
        const end = new Date(period.endDate);
        let current = new Date(start);

        // For check-in: block all dates from start to end (exclusive of end date)
        while (current < end) {
          blockedCheckInDays.push(current.toISOString().split("T")[0]);
          current.setDate(current.getDate() + 1);
        }
      });

      allBookedPeriods.forEach((period) => {
        const start = new Date(period.startDate);
        const end = new Date(period.endDate);
        let current = new Date(end);

        // For check-out: block all dates from start (exclusive) to end
        while (current > start) {
          blockedCheckOutDays.push(current.toISOString().split("T")[0]);
          current.setDate(current.getDate() - 1);
        }
      });

      // Remove duplicates
      const uniqueCheckInBlocked = [...new Set(blockedCheckInDays)].sort();
      const uniqueCheckOutBlocked = [...new Set(blockedCheckOutDays)].sort();

      setCheckInAvailability(uniqueCheckInBlocked);
      setCheckOutAvailability(uniqueCheckOutBlocked);
      setHasError(false);
      setErrorMessage("");
    } catch (error) {
      console.error("Error loading booked dates:", error);
      setHasError(true);
      setErrorMessage(error.message || "Failed to load availability data");
      // Set empty arrays to show all dates as available (safer than blocking everything)
      setCheckInAvailability([]);
      setCheckOutAvailability([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate, targetCalendarId, propertyType]);

  // Load booked dates for the current month
  useEffect(() => {
    loadBookedDates();
  }, [loadBookedDates]);

  async function handleDateClick(date) {
    if (selectionPhase === "checkin") {
      handleCheckInSelection(date);
    } else {
      handleCheckOutSelection(date);
    }
  }

  const handleCheckInSelection = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const today = new Date();
    const daysInFuture = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

    // Check if date is within allowed future range
    if (daysInFuture > bookingBounds.MAX_FUTURE_DAYS) {
      setStatus(
        `Cannot book more than ${bookingBounds.MAX_FUTURE_DAYS} days in advance`,
      );
      return;
    }

    // Check if date is available for check-in
    if (checkInAvailability.includes(dateStr)) {
      setStatus("This date is not available for check-in");
      return;
    }

    setCheckInDate(date);
    setStartDate(date); // Keep for backward compatibility
    setCheckOutDate(null);
    setEndDate(null);
    setSelectionPhase("checkout");
    setStatus("Select your check-out date");
  };

  const handleCheckOutSelection = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");

    // Check if date is available for check-out
    if (checkOutAvailability.includes(dateStr)) {
      setStatus("This date is not available for check-out");
      return;
    }

    // Check if check-out is after check-in
    if (date <= checkInDate) {
      setStatus("Check-out date must be after check-in date");
      return;
    }

    // Check booking duration limit
    const daysDifference = Math.ceil(
      (date - checkInDate) / (1000 * 60 * 60 * 24),
    );
    if (daysDifference > bookingBounds.MAX_BOOKING_DURATION) {
      setStatus(
        `Maximum booking duration is ${bookingBounds.MAX_BOOKING_DURATION} nights`,
      );
      return;
    }

    // Check if any dates in the range are booked
    const dateRange = getDatesInRange(checkInDate, date);
    const hasBookedDates = dateRange.some((dateInRange) =>
      checkInAvailability.includes(format(dateInRange, "yyyy-MM-dd")),
    );

    if (hasBookedDates) {
      setStatus("Selected range contains unavailable dates");
      return;
    }

    setCheckOutDate(date);
    setEndDate(date); // Keep for backward compatibility
    setStatus("Click 'Book' to continue.");
  };

  const handleBackToCheckIn = () => {
    setSelectionPhase("checkin");
    setCheckInDate(null);
    setCheckOutDate(null);
    setStartDate(null);
    setEndDate(null);
    setStatus("Select your check-in date");
  };

  // Function to open the booking modal
  const handleBookClick = () => {
    if (startDate && endDate) {
      setIsModalOpen(true);
    }
  };

  async function handleBookingConfirm(bookingData) {
    try {
      // For testing purposes, allow booking without date selection
      if (!startDate || !endDate) {
        try {
          const emailSent = await sendBookingConfirmationEmail(
            bookingData.rentalName,
            bookingData.name,
            new Date(),
            bookingData.email,
            bookingData.rentalStart,
            bookingData.rentalEnd,
          );

          if (emailSent) {
            setStatus("Test email sent successfully! Check your email.");
          } else {
            setStatus("Failed to send test email. Check console for errors.");
          }
        } catch (error) {
          console.error("Error calling sendBookingConfirmationEmail:", error);
          setStatus("Error testing email service. Check console for details.");
        }
        return Promise.resolve();
      }

      const startDateStr = format(startDate, "yyyy-MM-dd");
      const endDateStr = format(endDate, "yyyy-MM-dd");

      // Book the entire date range in one call
      const res = await fetch(`${API_URL}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: startDateStr,
          endDate: endDateStr,
          name: bookingData.name,
          email: bookingData.email,
          calendarId: targetCalendarId,
          propertyId,
        }),
      });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Booking failed");
      }

      await logBookingPayment({
        name: bookingData.name,
        email: bookingData.email,
        propertyDisplayName: propertyConfig?.displayName || propertyType,
        rentalStart: startDate,
        rentalEnd: endDate,
        totalAmount: bookingData.totalAmount,
        paymentMethod: bookingData.paymentMethod,
      });

      setStatus("Booked successfully! Check your email for confirmation.");

      loadBookedDates();
      setCheckInDate(null);
      setCheckOutDate(null);
      setStartDate(null);
      setEndDate(null);
      setSelectionPhase("checkin");

      sendBookingConfirmationEmail(
        bookingData.rentalName,
        bookingData.name,
        new Date(),
        bookingData.email,
        bookingData.rentalStart || startDateStr,
        bookingData.rentalEnd || endDateStr,
        bookingData.pricingModel ||
          propertyConfig?.pricingModel ||
          "gift_economy",
        bookingData.totalAmount || 0,
        bookingData.paymentMethod || "none",
      ).then((emailSent) => {
        if (!emailSent) {
          console.warn("Booking successful but email failed to send");
        }
      });

      return Promise.resolve();
    } catch (error) {
      console.error("Booking error:", error);
      throw error;
    }
  }

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  // Generate calendar days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const isBooked = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    if (selectionPhase === "checkin" || !checkInDate) {
      return checkInAvailability.includes(dateStr);
    } else {
      // When selecting check-out, check both check-out blocked dates and dates before check-in
      if (date <= checkInDate) return true;
      if (checkOutAvailability.includes(dateStr)) return true;

      // Also check if any dates in the range would be blocked
      const dateRange = getDatesInRange(checkInDate, date);
      const hasBookedDates = dateRange.some((dateInRange) =>
        checkInAvailability.includes(format(dateInRange, "yyyy-MM-dd")),
      );
      return hasBookedDates;
    }
  };

  const isInRange = (date) => {
    if (!checkInDate || !checkOutDate) return false;
    return date > checkInDate && date < checkOutDate;
  };

  const isCheckInDate = (date) => {
    return checkInDate && isSameDay(date, checkInDate);
  };

  const isCheckOutDate = (date) => {
    return checkOutDate && isSameDay(date, checkOutDate);
  };

  return (
    <div className="booking-calendar">

      {/* Phase Indicator */}
      <div className="phase-indicator">
        <div
          className={`phase-step ${
            selectionPhase === "checkin"
              ? "active"
              : checkInDate
                ? "completed"
                : ""
          }`}
        >
          <span className="phase-number">1</span>
          <span className="phase-label">Select Check-in</span>
        </div>
        <div
          className={`phase-step ${
            selectionPhase === "checkout"
              ? "active"
              : checkOutDate
                ? "completed"
                : ""
          }`}
        >
          <span className="phase-number">2</span>
          <span className="phase-label">Select Check-out</span>
        </div>
      </div>

      {/* Calendar Header */}
      <div className="calendar-header">
        <button onClick={prevMonth}>&lt; Previous</button>
        <h4>{format(currentDate, "MMMM yyyy")}</h4>
        <button onClick={nextMonth}>Next &gt;</button>
      </div>

      <div className="calendar-grid">
        {/* Day headers */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className="day-header">
            {day}
          </div>
        ))}

        {/* Loading State */}
        {isLoading && (
          <div className="calendar-loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading availability...</p>
          </div>
        )}

        {/* Error State */}
        {hasError && !isLoading && (
          <div className="calendar-error-overlay">
            <div className="error-icon">⚠️</div>
            <p className="error-message">{errorMessage}</p>
            <button className="retry-button" onClick={() => loadBookedDates()}>
              Retry
            </button>
          </div>
        )}

        {/* Calendar days - only show when not loading and no error */}
        {!isLoading &&
          !hasError &&
          days.map((day) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isBookedDate = isBooked(day);
            const isCheckInDateSelected = isCheckInDate(day);
            const isCheckOutDateSelected = isCheckOutDate(day);
            const isInRangeDate = isInRange(day);
            const isTodayDate = isToday(day);

            // Check if date is beyond the future booking limit
            const today = new Date();
            const daysInFuture = Math.ceil(
              (day - today) / (1000 * 60 * 60 * 24),
            );
            const isBeyondFutureLimit =
              daysInFuture > bookingBounds.MAX_FUTURE_DAYS;

            // Build CSS classes based on date state
            const dayClasses = [
              "calendar-day",
              isBookedDate && "booked",
              isCheckInDateSelected && "checkin-date",
              isCheckOutDateSelected && "checkout-date",
              isInRangeDate && "in-range",
              isTodayDate && "today",
              !isCurrentMonth && "other-month",
              isBeyondFutureLimit && "beyond-limit",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={day.toString()}
                onClick={() => handleDateClick(day)}
                disabled={
                  !isCurrentMonth || isBookedDate || isBeyondFutureLimit
                }
                className={dayClasses}
              >
                {format(day, "d")}
              </button>
            );
          })}
      </div>

      {/* Status Display */}
      {status && (
        <div
          className={`status-display ${
            status.includes("successfully") ? "success" : "info"
          }`}
        >
          {status}
        </div>
      )}

      <div className="legend">
        {/* <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color booked"></div>
            <span>Unavailable</span>
          </div>
          <div className="legend-item">
            <div className="legend-color checkin-date"></div>
            <span>Check-in</span>
          </div>
          <div className="legend-item">
            <div className="legend-color checkout-date"></div>
            <span>Check-out</span>
          </div>
          <div className="legend-item">
            <div className="legend-color in-range"></div>
            <span>Selected Range</span>
          </div>
          <div className="legend-item">
            <div className="legend-color today"></div>
            <span>Today</span>
          </div>
        </div> */}

        {/* Action Buttons */}
        {(checkInDate || checkOutDate) && !isLoading && !hasError && (
          <div className="action-buttons">
            <Button
              className="clear-button"
              onClick={() => {
                setCheckInDate(null);
                setCheckOutDate(null);
                setStartDate(null);
                setEndDate(null);
                setSelectionPhase("checkin");
                setStatus("Select your check-in date");
              }}
            >
              Clear Selection
            </Button>

            <Button
              className="book-button"
              onClick={handleBookClick}
              disabled={!checkInDate || !checkOutDate}
            >
              Book {displayName}
            </Button>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
        propertyType={propertyType}
        propertyId={propertyId}
        propertyConfig={propertyConfig}
        onConfirm={handleBookingConfirm}
      />
    </div>
  );
}
